// Growth labels and tones, shared by the Leads and Inquiries pages.

import type { ActivityFilter, ActivityStatus, Bucket, CheckResult, ContactStatus, InquiryStatus, LeadSignal, LeadSort, LeadStatus, LeadType, PlatformKind, PlatformStatus, PriceVsScoreLm, PublicPages, RegistrationStatus, VerifyFilter, VerifyState, VsScoreLm } from '@/api/types'
import type { BadgeTone } from '@/components/ui'

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  researching: 'Researching',
  contacted: 'Contacted',
  replied: 'Replied',
  trial: 'Trial',
  won: 'Won',
  lost: 'Lost',
  do_not_contact: 'Do not contact',
}

export const LEAD_STATUS_TONE: Record<LeadStatus, BadgeTone> = {
  new: 'accent',
  researching: 'neutral',
  contacted: 'neutral',
  replied: 'warn',
  trial: 'accent',
  won: 'good',
  lost: 'outline',
  do_not_contact: 'bad',
}

export const ACTIVITY_LABELS: Record<ActivityStatus, string> = { active: 'Active', stale: 'Stale', unknown: 'Unknown' }
export const ACTIVITY_TONE: Record<ActivityStatus, BadgeTone> = { active: 'good', stale: 'warn', unknown: 'neutral' }
export const NOT_CHECKED = 'Not checked'
export const ACTIVITY_FILTER_LABELS: Record<ActivityFilter, string> = { ...ACTIVITY_LABELS, unchecked: NOT_CHECKED }

export const VERIFY_LABELS: Record<VerifyState, string> = { verified: 'Verified', needs_review: 'Needs review', failed: 'Failed', unverified: 'Not verified' }
export const VERIFY_TONE: Record<VerifyState, BadgeTone> = { verified: 'good', needs_review: 'warn', failed: 'bad', unverified: 'outline' }
/** The Leads list's verification select. Needs verification is Needs review and Not checked yet together. */
export const VERIFY_FILTER_LABELS: Record<VerifyFilter, string> = { needs_verification: 'Needs verification', verified: 'Verified', needs_review: 'Needs review', unverified: 'Not checked yet', failed: 'Failed' }
export const SIGNAL_LABELS: Record<LeadSignal, string> = { registration_open: 'Registration open', current_schedule: 'Current schedule' }
export const REGISTRATION_LABELS: Record<RegistrationStatus, string> = { open: 'Open', upcoming: 'Upcoming', closed: 'Closed', none: 'None found' }
export const REGISTRATION_TONE: Record<RegistrationStatus, BadgeTone> = { open: 'good', upcoming: 'accent', closed: 'neutral', none: 'outline' }
export const CONTACT_LABELS: Record<ContactStatus, string> = { on_site: 'Contact on site', role_on_domain: 'Role address on site', hidden: 'Contact hidden', unconfirmed: 'Contact unconfirmed', none: 'No contact' }
export const CONTACT_TONE: Record<ContactStatus, BadgeTone> = { on_site: 'good', role_on_domain: 'good', hidden: 'neutral', unconfirmed: 'warn', none: 'outline' }
export const CHECK_RESULT_LABELS: Record<CheckResult, string> = { pass: 'Passed', info: 'For information', warn: 'Needs a look', fail: 'Failed' }
export const CHECK_RESULT_TONE: Record<CheckResult, BadgeTone> = { pass: 'good', info: 'neutral', warn: 'warn', fail: 'bad' }
/** What sits under a platform's name. Nothing when it's proven. */
export const PLATFORM_STATUS_NOTE: Record<PlatformStatus, string | null> = { confirmed: null, own_site: null, unconfirmed: 'Unconfirmed', none: null }

/** How a run's banner reads when it's a verification. */
export const VERIFY_RUN_WORDS = { done: 'Verification finished', doing: 'Verifying', name: 'Verification' }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A date with no time, as written. Going through Date would move it a day west of UTC. */
export function activityDay(day: string | null | undefined): string | null {
  const m = day?.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : null
}

export const BUCKET_LABELS: Record<Bucket, string> = {
  lost_site: 'Lost their site',
  no_solution: 'No real solution',
  weak_tool: 'Weak tool',
  team_app_only: 'Team app only',
  paying_same_or_more: 'Paying same or more for less',
  similar: 'Similar product',
  better_or_expensive: 'Better or expensive',
  review: 'To review',
  skip_enterprise: 'Skip',
  unknown: NOT_CHECKED,
}

export const BUCKET_TONE: Record<Bucket, BadgeTone> = {
  lost_site: 'good',
  no_solution: 'good',
  weak_tool: 'good',
  team_app_only: 'accent',
  paying_same_or_more: 'accent',
  similar: 'neutral',
  better_or_expensive: 'warn',
  review: 'neutral',
  skip_enterprise: 'bad',
  unknown: 'outline',
}

export const PLATFORM_KIND_LABELS: Record<PlatformKind, string> = {
  league_platform: 'League platform',
  rec_software: 'Rec software',
  team_app: 'Team app',
  registration_only: 'Registration only',
  tournament: 'Tournament',
  diy: 'Do it yourself',
}

export const PUBLIC_PAGES_LABELS: Record<PublicPages, string> = { yes: 'Public', partial: 'Partly public', no: 'Not public', unknown: 'Not known' }

export const VS_SCORELM_LABELS: Record<VsScoreLm, string> = { worse: 'Worse', similar: 'Similar', better: 'Better', different: 'Different', unknown: NOT_CHECKED }
export const VS_SCORELM_TONE: Record<VsScoreLm, BadgeTone> = { worse: 'good', similar: 'neutral', better: 'warn', different: 'neutral', unknown: 'outline' }
export const PRICE_VS_LABELS: Record<PriceVsScoreLm, string> = { cheaper: 'Cheaper', similar: 'Same price', more: 'Costs more', unknown: NOT_CHECKED }
export const PRICE_VS_TONE: Record<PriceVsScoreLm, BadgeTone> = { cheaper: 'warn', similar: 'neutral', more: 'good', unknown: 'outline' }

/** What ScoreLM charges: the one place these numbers live. */
export const SCORELM_BASELINE = {
  plans: [
    { name: 'Starter', monthly_usd: 29 },
    { name: 'Pro', monthly_usd: 59 },
    { name: 'Courtside', monthly_usd: 99 },
  ],
  terms: 'A month, flat per league, unlimited teams.',
} as const

/** A platform's price as one line. */
export function platformPrice(p: { price_summary: string | null; price_monthly_low_usd: number | null }): string {
  if (p.price_summary) return p.price_summary
  return p.price_monthly_low_usd === null ? 'Not known' : `From $${p.price_monthly_low_usd} a month`
}

export const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  youth: 'Youth',
  adult: 'Adult',
  church: 'Church',
  school: 'School',
  rec: 'Rec',
  other: 'Other',
}

export const LEAD_SORT_OPTIONS: Array<{ value: LeadSort; label: string }> = [
  { value: '-priority', label: 'Highest priority' },
  { value: 'bucket', label: 'Best bucket first' },
  { value: 'name', label: 'Name A–Z' },
  { value: '-created_at', label: 'Newest first' },
  { value: 'created_at', label: 'Oldest first' },
  { value: '-updated_at', label: 'Recently changed' },
  { value: '-last_contacted_at', label: 'Recently contacted' },
]

/** Platforms offered when adding a lead by hand. Any text is allowed. */
export const KNOWN_PLATFORMS = ['RecLeague', 'TeamLinkt', 'LeagueLobster', 'unknown']

export const INQUIRY_STATUS_LABELS: Record<InquiryStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  answered: 'Answered',
  spam: 'Spam',
}

export const INQUIRY_STATUS_TONE: Record<InquiryStatus, BadgeTone> = { new: 'accent', in_progress: 'warn', answered: 'good', spam: 'outline' }
